import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface HeroSlide {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  image: string;
  cta_text: string;
  cta_link: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Fetch all active hero slides
export function useHeroSlides() {
  return useQuery({
    queryKey: ["hero-slides"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("hero_slides")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data as HeroSlide[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Fetch all hero slides (including inactive - for admin)
export function useAllHeroSlides() {
  return useQuery({
    queryKey: ["hero-slides-all"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("hero_slides")
        .select("*")
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data as HeroSlide[];
    },
  });
}

// Create new slide
export function useCreateHeroSlide() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (slide: Omit<HeroSlide, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await (supabase as any)
        .from("hero_slides")
        .insert(slide)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hero-slides"] });
      queryClient.invalidateQueries({ queryKey: ["hero-slides-all"] });
    },
  });
}

// Update slide
export function useUpdateHeroSlide() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<HeroSlide> & { id: string }) => {
      const { data, error } = await (supabase as any)
        .from("hero_slides")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hero-slides"] });
      queryClient.invalidateQueries({ queryKey: ["hero-slides-all"] });
    },
  });
}

// Delete slide
export function useDeleteHeroSlide() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from("hero_slides")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hero-slides"] });
      queryClient.invalidateQueries({ queryKey: ["hero-slides-all"] });
    },
  });
}

// Default slides fallback (when DB is empty)
export const DEFAULT_HERO_SLIDES: HeroSlide[] = [
  {
    id: "1",
    title: "Timeless Elegance",
    subtitle: "Bridal Couture '24",
    description: "Exquisite handcrafted pieces celebrating Indian heritage",
    image: "/images/heritage/hero-bridal.jpg",
    cta_text: "Explore Collection",
    cta_link: "/shop?category=lehengas",
    display_order: 1,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: "2",
    title: "Woven Heritage",
    subtitle: "Banarasi & Kanjivaram",
    description: "Tradition meets timeless beauty in every weave",
    image: "/images/heritage/hero-charcoal.jpg",
    cta_text: "Shop Sarees",
    cta_link: "/shop?category=sarees",
    display_order: 2,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: "3",
    title: "Festive Splendor",
    subtitle: "Designer Collection",
    description: "Celebrate every moment with grace and grandeur",
    image: "/images/heritage/hero-modern.jpg",
    cta_text: "View All",
    cta_link: "/shop",
    display_order: 3,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];
